// ContactPage.jsx
import { useState } from 'react';
import { FaWhatsapp } from 'react-icons/fa';
import Footer from '../sections/web_core/Footer';
import SecondHeroSection from '../sections/web_core/SecondHeroSection';
import IMAGE from "../Assets/Courses/imgi_25_page-title.jpg";

/* =========================
   Section Wrapper
========================= */
const SectionWrapper = ({ children, className = "" }) => {
  return <section className={`w-full mb-[5px] ${className}`}>{children}</section>;
};

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const phoneNumber = "+92 339 155-2155"; // Management WhatsApp number
    const message = encodeURIComponent(
      `*New Contact Form Submission*\n
*Name:* ${formData.firstName} ${formData.lastName}
*Email:* ${formData.email}
*Phone:* ${formData.phone}\n
💬 *Message:*\n${formData.message}\n
*Sent on:* ${new Date().toLocaleString()}`
    );

    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(whatsappUrl, '_blank');
    alert('Opening WhatsApp to send your message to management!');
    setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-emerald-50 relative">

      {/* Floating WhatsApp Icon */}
      <a
        href="https://wa.me/923391552155"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-14 h-14 bg-green-500 hover:bg-green-600 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 animate-pulse"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp className="text-2xl" />
      </a>

      {/* Hero Section */}
      <SectionWrapper>
        <SecondHeroSection
          title="Contact"
          subtitle="Contact Us"
          bgImage={IMAGE}
          breadcrumbs={[
            { label: "Home", href: "/" },
            { label: "Contact" },
          ]}
        />
      </SectionWrapper>

      {/* Main Contact Content */}
      <SectionWrapper className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-emerald-100">
            <h2 className="text-3xl font-bold text-emerald-800 mb-6">Send Us A Message</h2>
            <p className="text-gray-600 mb-8">
              Fill this form and your message will be sent directly to management via WhatsApp.
            </p>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 mb-2 font-semibold">First Name *</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="First name"
                    className="w-full px-4 py-3 rounded-lg border border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition duration-300"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2 font-semibold">Last Name *</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Last name"
                    className="w-full px-4 py-3 rounded-lg border border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition duration-300"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-700 mb-2 font-semibold">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Email address"
                    className="w-full px-4 py-3 rounded-lg border border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition duration-300"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2 font-semibold">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="Phone number"
                    className="w-full px-4 py-3 rounded-lg border border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition duration-300"
                  />
                </div>
              </div>

              <div className="mb-8">
                <label className="block text-gray-700 mb-2 font-semibold">Your Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder="Type your message here..."
                  className="w-full px-4 py-3 rounded-lg border border-emerald-200 focus:outline-none focus:ring-2 focus:ring-emerald-300 transition duration-300"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-green-900/95 via-green-800 to-green-900 text-white font-semibold py-4 rounded-lg hover:from-emerald-700 hover:to-emerald-600 transition duration-300 shadow-lg flex items-center justify-center gap-3"
              >
                <FaWhatsapp className="text-xl" />
                Send via WhatsApp
              </button>
            </form>
          </div>

          {/* Contact Info & Map */}
          <div className="space-y-8">

            {/* Contact Info */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-emerald-100">
              <h2 className="text-3xl font-bold text-emerald-800 mb-8">Contact Info</h2>

              <div className="space-y-8">
                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Phone Number</h3>
                    <a href="tel:00923391552155" className="text-emerald-600 hover:text-emerald-800 text-lg">
                      +92 339 155-2155
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Email Address</h3>
                    <a href="mailto:ceo@onlinequranandislamicinstitue.com" className="text-emerald-600 hover:text-emerald-800 text-lg">
                      ceo@onlinequranandislamicinstitue.com
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Visit Us</h3>
                    <p className="text-gray-600 text-lg">3rd floor, Kiyani Plaza Murree Road, Rawalpindi</p>
                  </div>
                </div>
              </div>

              {/* Direct WhatsApp */}
              <div className="mt-10">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Chat Directly</h3>
                <a
                  href="https://wa.me/923391552155"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition duration-300"
                >
                  <FaWhatsapp className="text-xl" />
                  Click here to start WhatsApp chat
                </a>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-emerald-100">
              <h2 className="text-3xl font-bold text-emerald-800 mb-6">Our Location</h2>
              <div className="bg-emerald-50 rounded-xl overflow-hidden border border-emerald-200 h-96 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-100 to-emerald-200"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="relative">
                    <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                      <div className="w-4 h-4 bg-white rounded-full"></div>
                    </div>
                    <div className="absolute -top-2 -right-2 w-4 h-4 bg-emerald-400 rounded-full animate-ping"></div>
                  </div>
                </div>
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
                  <div className="bg-white px-6 py-3 rounded-lg shadow-lg border border-emerald-200">
                    <p className="text-emerald-800 font-semibold text-center">Rawalpindi, Pakistan</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </SectionWrapper>

      <Footer />
    </div>
  );
};

export default ContactPage;